/**
 * Registry of keypoint detection models bundled with the jamcam app.
 *
 * To add a new model:
 *   1. Convert the trained .keras to a TF.js graph model under
 *      `pytrain_keypoint/<your_model_dir>/`.
 *   2. Add a copy entry for it in `apps/jamcam/webpack.config.js` and
 *      `apps/jamcam/project.json` so the build copies it to
 *      `assets/models/<your_model_dir>/`.
 *   3. Append an entry below — the dropdown on the Developer settings
 *      tab picks these up automatically.
 *
 * `DEFAULT_KEYPOINT_MODEL_URL` is what new installs / config resets use.
 */

export interface KeypointModelOption {
  /** Stable id used internally (also in URL). */
  id: string;
  /** Human-readable label shown in the dropdown. */
  label: string;
  /** Path served to TF.js. */
  url: string;
  /** Short description shown under the label. */
  description?: string;
}

export const KEYPOINT_MODELS: KeypointModelOption[] = [
  {
    id: 'aws_v4_edges',
    label: 'AWS v4 edges (320×320, MobileNetV2)',
    url: '/assets/models/web_dots_aws_v4_model/model.json',
    description:
      '16-keypoint edges-only square model, retrained on the full AWS dataset (newest default).',
  },
  {
    id: 'square_edges_v3',
    label: 'Square edges v3 (320×320, MobileNetV2)',
    url: '/assets/models/web_dots_v3_model/model.json',
    description: '16-keypoint edges-only square model. Best-quality default.',
  },
  {
    id: 'rect_v1',
    label: 'Rect v1 (320×96, MobileNetV2)',
    url: '/assets/models/web_rect_v1_model/model.json',
    description: '16-keypoint edges-only rectangular model. Faster, lower-res.',
  },
  {
    id: 'rect_v1p1',
    label: 'Rect v1.1 (320×96, MobileNetV2)',
    url: '/assets/models/web_rect_v1p1_model/model.json',
    description: '16-keypoint edges-only rectangular model, retrained variant.',
  },
];

export const DEFAULT_KEYPOINT_MODEL_URL = KEYPOINT_MODELS[0].url;

/** Lookup helper — returns undefined when the URL is a custom value. */
export function findKeypointModel(
  url: string
): KeypointModelOption | undefined {
  return KEYPOINT_MODELS.find((m) => m.url === url);
}
